export interface PrimisAdStart {
  playerApiId: string
  impValue: number
  servingFee: number
  playerWidth?: number
  playerHeight?: number
}

interface PrimisPlayerApi {
  playerApiId?: unknown
  addEventListener: (
    name: string,
    callback: (data?: unknown) => void
  ) => unknown
  removeEventListener?: (handle: unknown) => void
}

const isPrimisPlayer = (value: unknown): value is PrimisPlayerApi =>
  typeof value === 'object' &&
  value !== null &&
  'addEventListener' in value &&
  typeof value.addEventListener === 'function'

const readNumber = (data: object, key: string): number | undefined => {
  const value: unknown = Reflect.get(data, key)
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function readAdStart(playerApiId: string, data: unknown): PrimisAdStart | null {
  if (typeof data !== 'object' || data === null) return null
  const impValue = readNumber(data, 'impValue')
  const servingFee = readNumber(data, 'servingFee')
  if (impValue === undefined || servingFee === undefined) return null

  return {
    playerApiId,
    impValue,
    servingFee,
    playerWidth: readNumber(data, 'playerWidth'),
    playerHeight: readNumber(data, 'playerHeight'),
  }
}

// Primis announces each player once, so register before any player script
// loads. The event can come from Primis's own iframe, hence no instanceof.
export function onPrimisAdStart(
  listener: (adStart: PrimisAdStart) => void
): () => void {
  const target = window.top ?? window
  const stops: Array<() => void> = []

  const handleInit = (initEvent: Event) => {
    const player: unknown = 'detail' in initEvent ? initEvent.detail : undefined
    if (!isPrimisPlayer(player)) return
    const playerApiId =
      player.playerApiId === undefined ? '' : String(player.playerApiId)

    const handle = player.addEventListener('adStarted', (data) => {
      const adStart = readAdStart(playerApiId, data)
      if (adStart) listener(adStart)
    })
    stops.push(() => player.removeEventListener?.(handle))
  }

  target.addEventListener('primisPlayerInit', handleInit)
  return () => {
    target.removeEventListener('primisPlayerInit', handleInit)
    stops.forEach((stop) => stop())
  }
}
